import { can } from '../utils/memberPermissions';
import { useCustomFields } from '../components/IndustryFields';
import CustomFieldFilter from '../components/CustomFieldFilter';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, MessageCircle, X, Check, Clock, Users, SlidersHorizontal } from 'lucide-react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { leadsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import FormError from '../components/FormError';
import { formatFollowUp } from '../utils/followUps';

import { STATUS_OPTIONS, getLeadStatus } from '../utils/leadStatuses';

const SOURCE_OPTIONS = [
  ['whatsapp', 'WhatsApp'], ['instagram', 'Instagram'], ['facebook', 'Facebook'],
  ['walk_in', 'Walk-in'], ['referral', 'Referral'], ['other', 'Other'],
];
const URGENCY_OPTIONS = [['now', 'Now'], ['soon', 'Soon'], ['browsing', 'Just browsing']];

const Leads = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchButton = useRef(null);
  const closeSearch = () => {
    setSearchQuery('');
    setShowSearch(false);
    searchButton.current?.focus();
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = STATUS_OPTIONS.some(status => status.value === searchParams.get('status')) ? searchParams.get('status') : '';
  const setStatusFilter = (status) => {
    const next = new URLSearchParams(searchParams);
    if (status) next.set('status', status); else next.delete('status');
    setSearchParams(next);
  };
  const [sourceFilter, setSourceFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState(null);
  const customFields = useCustomFields();
  useEffect(()=>{if(fieldFilter && customFields.isSuccess && !customFields.data.some(field=>field.id===fieldFilter.fieldId))setFieldFilter(null);},[fieldFilter,customFields.isSuccess,customFields.data]);
  const myLeads = Boolean(user?.teamInboxEnabled) && searchParams.get('owner') === 'me';
  const setMyLeads = (mine) => {
    const next = new URLSearchParams(searchParams);
    if (mine) next.set('owner', 'me'); else next.delete('owner');
    setSearchParams(next);
  };
  const [showFilters, setShowFilters] = useState(false);
  const teamInboxEnabled = Boolean(user?.teamInboxEnabled);

  const queryClient = useQueryClient();

  const {
    data: leads = [],
    isLoading: loading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['leads', searchQuery, statusFilter, myLeads, sourceFilter, urgencyFilter, fieldFilter],
    queryFn: async () => {
      const params = { ...fieldFilter };
      if (myLeads) params.assignedToMe = true;
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (urgencyFilter) params.urgency = urgencyFilter;
      const { data } = await leadsAPI.getAll(params);
      return data || [];
    },
    placeholderData: keepPreviousData,
  });

  const error = isError ? 'Failed to load enquiries' : null;
  const [actionError, setActionError] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const updateStatusMutation = useMutation({
    mutationFn: ({ leadId, status }) => leadsAPI.update(leadId, { status }),
    onMutate: () => { setActionError(''); setConfirmation(''); },
    onError: () => setActionError('Could not update status. Please try again.'),
    onSuccess: (_res, { leadId, status }) => {
      setConfirmation(`Enquiry status saved: ${getLeadStatus(status).label}.`);
      // Optimistic-ish update so UI doesn't wait for refetch.
      queryClient.setQueriesData({ queryKey: ['leads'] }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((lead) => (lead.id === leadId ? { ...lead, status } : lead));
      });
      queryClient.setQueryData(['lead', leadId], (old) => (old ? { ...old, status } : old));
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['salesReport'] });
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    },
  });

  const markContactedMutation = useMutation({
    onMutate: () => { setActionError(''); setConfirmation(''); },
    onError: () => setActionError('Could not mark contacted. Please try again.'),
    mutationFn: async ({ leadId }) => {
      const { data } = await leadsAPI.markWhatsappContactNow(leadId);
      return { leadId, lastWhatsappContactAt: data?.lastWhatsappContactAt || new Date().toISOString() };
    },
    onSuccess: ({ leadId, lastWhatsappContactAt }) => {
      setConfirmation('Contact logged. Enquiry status and follow-up reminders are unchanged.');
      queryClient.setQueriesData({ queryKey: ['leads'] }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((lead) =>
          lead.id === leadId ? { ...lead, lastWhatsappContactAt } : lead
        );
      });
      queryClient.setQueryData(['lead', leadId], (old) => (old ? { ...old, lastWhatsappContactAt } : old));
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['salesReport'] });
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    },
  });

  const handleStatusChange = (leadId, newStatus) => {
    updateStatusMutation.mutate({ leadId, status: newStatus });
  };

  const handleMarkContactedToday = async (leadId) => {
    const res = await markContactedMutation.mutateAsync({ leadId });
    return res?.lastWhatsappContactAt;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSourceFilter('');
    setUrgencyFilter('');
    setFieldFilter(null);
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('owner');
    setSearchParams(next);
  };

  const activeFilters = [
    fieldFilter && { key: 'field', label: `Custom field: ${fieldFilter.fieldValue}${fieldFilter.fieldEnd ? ` – ${fieldFilter.fieldEnd}` : ''}`, clear: () => setFieldFilter(null) },
    statusFilter && { key: 'status', label: getLeadStatus(statusFilter).label, clear: () => setStatusFilter('') },
    sourceFilter && { key: 'source', label: SOURCE_OPTIONS.find(([value]) => value === sourceFilter)?.[1], clear: () => setSourceFilter('') },
    urgencyFilter && { key: 'urgency', label: URGENCY_OPTIONS.find(([value]) => value === urgencyFilter)?.[1], clear: () => setUrgencyFilter('') },
    myLeads && { key: 'owner', label: 'My enquiries', clear: () => setMyLeads(false) },
  ].filter(Boolean);
  const hasFilters = searchQuery || activeFilters.length > 0;

  return (
    <div className="space-y-4">
      {teamInboxEnabled && <div className="grid grid-cols-2 rounded-xl bg-gray-100 p-1" aria-label="Lead ownership">
        <button aria-pressed={!myLeads} className={`min-h-[44px] rounded-lg px-3 py-2 text-sm font-semibold transition-all ${!myLeads ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setMyLeads(false)}>All enquiries</button>
        <button aria-pressed={myLeads} className={`min-h-[44px] rounded-lg px-3 py-2 text-sm font-semibold transition-all ${myLeads ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} onClick={() => setMyLeads(true)}>My enquiries</button>
      </div>}
      <Link to="/contacts" className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-primary-700 shadow-sm hover:border-primary-200 hover:bg-primary-50">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50"><Users aria-hidden="true" className="h-4 w-4" /></span>
        <span className="min-w-0"><span className="block text-sm font-semibold">Contacts</span><span className="block text-xs text-gray-500">View people and their related enquiries</span></span>
      </Link>
      {/* Search Bar */}
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        <button
          ref={searchButton}
          type="button"
          aria-label={showSearch ? 'Close search' : 'Search enquiries'}
          aria-expanded={showSearch}
          aria-controls="lead-search"
          onClick={() => showSearch ? closeSearch() : setShowSearch(true)}
          className="inline-flex min-h-[40px] shrink-0 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold text-gray-700 hover:bg-gray-100"
        >
          {showSearch ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
          {!showSearch && <span>Search</span>}
        </button>
        {showSearch && <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            id="lead-search"
            autoFocus
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); closeSearch(); } }}
            aria-label="Search enquiries by contact, company, requirements or custom fields"
            placeholder="Search enquiries…"
            className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
          {searchQuery && (
            <button
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>}
        <button
          aria-label="Filter enquiries"
          aria-controls="lead-status-filters"
          aria-expanded={showFilters}
          onClick={() => setShowFilters(!showFilters)}
          className={`inline-flex min-h-[40px] shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
            showFilters || activeFilters.length > 0
              ? 'bg-primary-50 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
          Filters{activeFilters.length > 0 ? ` (${activeFilters.length})` : ''}
        </button>
      </div>

      {/* Status Filter */}
      {showFilters && (
        <div id="lead-status-filters" className="bg-white rounded-lg p-3 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Filter by status</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((status) => (
              <button
                key={status.value}
                aria-pressed={statusFilter === status.value}
                onClick={() => setStatusFilter(statusFilter === status.value ? '' : status.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  statusFilter === status.value
                    ? status.className
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <label className="text-sm font-medium text-gray-700">
              Source
              <select aria-label="Filter by source" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="mt-1 w-full p-2 rounded-lg border border-gray-300 bg-white">
                <option value="">All sources</option>
                {SOURCE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Urgency
              <select aria-label="Filter by urgency" value={urgencyFilter} onChange={(e) => setUrgencyFilter(e.target.value)} className="mt-1 w-full p-2 rounded-lg border border-gray-300 bg-white">
                <option value="">All urgency levels</option>
                {URGENCY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </div>
          <CustomFieldFilter onApply={setFieldFilter} />
        </div>
      )}

      {/* Active Filter Badge */}
      {hasFilters && (
        <div className="flex flex-wrap items-center gap-2" aria-label="Active enquiry filters">
          {activeFilters.map((filter) => (
            <span key={filter.key} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700">
              {filter.label}
              <button aria-label={`Clear ${filter.key} filter`} onClick={filter.clear} className="compact-control rounded-full p-1 hover:text-primary-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          ))}
          <button onClick={clearFilters} className="px-2 py-1 text-sm text-primary-600 hover:text-primary-700">Clear all</button>
        </div>
      )}

      {error && (
        <div className="space-y-2">
          <FormError message={error} />
          <button type="button" onClick={() => refetch()} className="px-3 py-2 rounded-lg border bg-white text-gray-700">Try again</button>
        </div>
      )}

      <FormError message={actionError} />
      <p role="status" aria-live="polite" className={confirmation ? 'text-sm text-green-800' : 'sr-only'}>{confirmation}</p>
      {isFetching && !loading && <p role="status" className="text-sm text-gray-600">Updating results… The previous results are shown until the update finishes.</p>}

      {/* Loading State */}
      {loading && (
        <div className="space-y-3" role="status" aria-label="Loading enquiries">
          <span className="sr-only">Loading enquiries…</span>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !isError && !isFetching && leads.length === 0 && (
        <div className="text-center py-12">
          <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No enquiries found
          </h3>
          <p className="text-gray-600 mb-4">
            {hasFilters
              ? 'Try adjusting your search or filters'
              : myLeads ? 'No enquiries are assigned to you. Open All enquiries to claim an unassigned enquiry.' : 'Start by adding your first enquiry'}
          </p>
          {!hasFilters && !myLeads && can(user, 'createEnquiries') && (
            <Link
              to="/leads/new"
              className="inline-flex items-center gap-2 bg-primary-600 text-white p-3 rounded-lg text-sm font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors"
            >
              Add enquiry
            </Link>
          )}
        </div>
      )}

      {/* Leads List */}
      {!loading && leads.length > 0 && (
        <div className="space-y-3" aria-busy={isFetching}>
          <div className="flex items-center justify-between px-1">
            <h2 className="text-sm font-semibold text-gray-900">{leads.length} {leads.length === 1 ? 'enquiry' : 'enquiries'}</h2>
            {hasFilters && <span className="text-xs text-gray-500">Filtered results</span>}
          </div>
          {leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              canWork={can(user, 'editEnquiries') && (!teamInboxEnabled || user?.role === 'owner' || lead.assignedUserId === user?.id)}
              savingStatus={updateStatusMutation.isPending && updateStatusMutation.variables?.leadId === lead.id}
              onStatusChange={handleStatusChange}
              onMarkContactedToday={handleMarkContactedToday}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LeadCard = ({ lead, canWork, savingStatus, onStatusChange, onMarkContactedToday }) => {
  const [markingContacted, setMarkingContacted] = useState(false);

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-all hover:border-gray-300 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <Link to={`/leads/${lead.id}`} className="group min-w-0 flex-1">
          <h3 className="text-base font-semibold leading-5 text-gray-900 break-words transition-colors group-hover:text-primary-700">{lead.title || lead.productOrService || 'Sales enquiry'}</h3>
          <p className="mt-1 truncate text-sm text-gray-600">{lead.name}{lead.companyName ? ` · ${lead.companyName}` : ''}</p>
        </Link>
        <div className="relative flex min-h-[40px] max-w-[42%] shrink-0 items-center rounded-full focus-within:ring-2 focus-within:ring-primary-500">
          <span aria-hidden="true" className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold ${getLeadStatus(lead.status).className}`}>
            {getLeadStatus(lead.status).label}
            <span className="h-1.5 w-1.5 -translate-y-0.5 rotate-45 border-b border-r border-current" />
          </span>
          <select
            aria-label={`Enquiry status for ${lead.name}`}
            title="Sales stage only; reminders are scheduled separately."
            value={lead.status}
            disabled={!canWork || savingStatus}
            onChange={(event) => onStatusChange(lead.id, event.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
          >
            {!STATUS_OPTIONS.some((status) => status.value === lead.status) && <option value={lead.status}>{getLeadStatus(lead.status).label}</option>}
            {STATUS_OPTIONS.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
          </select>
          {savingStatus && <p role="status" className="sr-only">Saving status…</p>}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 pt-2">
        <p className={`mr-auto flex min-w-0 items-center gap-1.5 text-xs font-medium ${lead.nextFollowUp ? 'text-amber-700' : 'text-gray-500'}`}>
          <Clock className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{lead.nextFollowUp ? `Next task: ${formatFollowUp(lead.nextFollowUp)}` : 'No task scheduled'}</span>
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={async () => {
              try {
                setMarkingContacted(true);
                await onMarkContactedToday(lead.id);
              } catch (err) {
                console.error('Failed to mark contacted today:', err);
              } finally {
                setMarkingContacted(false);
              }
            }}
            disabled={markingContacted || !canWork}
            className="inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
            title="Record that you contacted this enquiry; this does not change their stage or complete a reminder."
            aria-label={`Log contact with ${lead.name}`}
          >
            <Check className="w-4 h-4" />
            {markingContacted ? 'Logging…' : 'Log contact'}
          </button>

          {lead.whatsappUrl && (<a
            aria-label="Open WhatsApp"
            href={lead.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold text-green-700 transition-colors hover:bg-green-50 active:bg-green-100"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            WhatsApp
          </a>)}


        </div>
      </div>
    </article>
  );
};

export default Leads;
